import React, { useRef, useState } from 'react';
import {
  Box,
  Divider,
  Typography,
  IconButton,
  Button,
  Switch,
} from '@common-ui-library/atom';
import {
  AddCircleOutlineIcon,
  FilterIcon,
  SaveAltIcon,
  useI18n,
  useBIEvents,
} from '@common-ui-library/shared';
import { FormControlLabel } from '@mui/material';
import { Badge } from '@common-ui-library/atom';
import { SearchField } from '@common-ui-library/molecule';
import { useUserManagementStore } from '../Store/useUserManagementStore';
import { useUserManagementContext } from '../UserManagementContext';
import { EntityNameMapping } from '../../Utils/utils';
import { handleExportToCsv } from '../UMUtils/UMHeaderUtils';
import { userManagementColumnProps } from '../Props';
import { useShallow } from 'zustand/react/shallow';
import { entityFilterConfigs } from '../Props/entityFilterConfigs';
import FilterPopover from './FilterPopover';
import {
  getSearchEvent,
  getExportCsvEvent,
  getCreateStartEvent,
  getFilterOpenEvent,
  getFilterApplyEvent,
  getFilterClearEvent,
  getShowInactiveToggleEvent,
} from '../../Utils/biEvents';
import { UserManagementEntity } from '../../Utils/types';

const NO_EXPORT_COLUMNS = ['selectCheckbox', 'program']

const UserManagementEntityHeader = () => {
  const { 
    items, 
    setIsAddEntityOpen, 
    userManagementEntity, 
    fetchEntities, 
    setQueryState,
    queryState,
    dropdownOptions,
    pagination,
  } = useUserManagementStore(
    useShallow((state) => ({
      items: state.items,
      setIsAddEntityOpen: state.setIsAddEntityOpen,
      userManagementEntity: state.userManagementEntity,
      fetchEntities: state.fetchEntities,
      setQueryState: state.setQueryState,
      queryState: state.queryState,
      dropdownOptions: state.dropdownOptions,
      pagination: state.pagination,
    }))
  );
  const { service } = useUserManagementContext();
  const { t } = useI18n();
  const { sendBIEvent } = useBIEvents();
  
  // Track previous filter count for clear event
  const prevFilterCountRef = useRef(0);

  // Filter popover state
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const filterPopoverOpen = Boolean(filterAnchorEl);

  // Get filter config for current entity
  const filterConfig = userManagementEntity ? entityFilterConfigs[userManagementEntity] : undefined;
  
  // Current filters as string array
  const currentFilters = (queryState?.filters as string[]) || [];

  // Derive toggle state from filters (single source of truth)
  const showInactiveUsers = currentFilters.some(f => f === 'account_status=all');
  
  // Filters visible in popover (exclude account_status - controlled by toggle)
  const popoverFilterCount = currentFilters.filter(f => !f.startsWith('account_status=')).length;

  const entityTitle = userManagementEntity ? EntityNameMapping[userManagementEntity] : '';

  const handleAddClick = () => {
    setIsAddEntityOpen(true);
    // Emit create start BI event
    if (userManagementEntity) {
      sendBIEvent(getCreateStartEvent(userManagementEntity), {
        path: window.location.pathname,
      });
    }
  };

  const onExportToCsv = () => {
    if (!userManagementEntity) return;
    
    // Use selected items if any, otherwise all items
    const selectedItems = items.filter(item => item.selected);
    const itemsToExport = selectedItems.length > 0 ? selectedItems : items;
    
    const columns = userManagementColumnProps[userManagementEntity] as { key: string; header: string; exportValue?: (item: UserManagementEntity) => string }[];
    const data = itemsToExport.map((item) => {
      const row: Record<string, string> = {};
      for (const column of columns) {
        if (!NO_EXPORT_COLUMNS.includes(column.key)) {
          row[column.header] = column.exportValue?.(item) || String((item as unknown as Record<string, unknown>)[column.key] ?? '');
        }
      }
      return row;
    });
    handleExportToCsv(data, `${entityTitle}_export`);
    
    // Emit export CSV BI event
    sendBIEvent(getExportCsvEvent(userManagementEntity));
  };

  const onSearchChange = async (searchQuery: string) => {
      if (!userManagementEntity) return;
      setQueryState({ search: searchQuery });
      await fetchEntities(service, userManagementEntity, { search: searchQuery, page: 0 });
      
      // Emit search BI event
      sendBIEvent(getSearchEvent(userManagementEntity), {
        search_term: searchQuery,
      });
  }

  const onFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
    
    // Emit filter open BI event
    if (userManagementEntity) {
      sendBIEvent(getFilterOpenEvent(userManagementEntity), {
        count: {total: popoverFilterCount},
      });
    }
  };

  const onFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const onFilterApply = async (filters: string[]) => {
    if (!userManagementEntity) return;
    
    // Emit filter BI event (apply or clear)
    if (filters.length > 0) {
      sendBIEvent(getFilterApplyEvent(userManagementEntity));
    } else if (prevFilterCountRef.current > 0) {
      sendBIEvent(getFilterClearEvent(userManagementEntity), {
        count: {total: prevFilterCountRef.current},
      });
    }
    prevFilterCountRef.current = filters.length;
    
    setQueryState({ filters: filters.length > 0 ? filters : null });
    await fetchEntities(service, userManagementEntity, { 
      filters: filters.length > 0 ? filters : undefined, 
      page: 0 
    });
  };

  // Toggle handler for showing inactive users (providers only for now)
  const onToggleInactiveUsers = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!userManagementEntity) return;
    
    const checked = event.target.checked;
    
    // Emit toggle BI event
    sendBIEvent(getShowInactiveToggleEvent(userManagementEntity), {
      button_on_after_click: checked ? "on" : "off",
    });
    
    // Build filters: merge with existing filters, add/remove account_status
    const otherFilters = currentFilters.filter(f => !f.startsWith('account_status='));
    const newFilters = checked 
      ? [...otherFilters, 'account_status=all']
      : otherFilters; // Default API behavior is active only
      
    setQueryState({ filters: newFilters.length > 0 ? newFilters : null });
    await fetchEntities(service, userManagementEntity, { 
      filters: newFilters.length > 0 ? newFilters : undefined, 
      page: 0 
    });
  };

  return (
    <>
      <Box sx={{ p: 1, bgcolor: 'background.paper' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="h6">{entityTitle}</Typography>
          </Box>
          {/* Show inactive users toggle - providers only - an intentional decision to not over abstract when this is a minimal, be aware in the future if this change */}
          {userManagementEntity === 'providers' && (
            <FormControlLabel
            
            control={
                <Switch 
                  checked={showInactiveUsers}
                  onChange={onToggleInactiveUsers}
                  sx={{ mr: 1 }}
                />
              }
              label={t('labels.showInactiveUsers')}
              labelPlacement="end"
            />
          )}
        </Box>
        <Divider sx={{ backgroundColor: 'grey.300', my: 2 }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              width: '400px',
            }}
          >
            <SearchField
              searchLabel={`${t(`placeholders.searchEntity`, { entity: entityTitle })}`}
              handleSearchChange={onSearchChange}
            />
            {filterConfig && (
              <IconButton onClick={onFilterClick}>
                <Badge 
                  badgeContent={popoverFilterCount} 
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                    }
                  }}
                  invisible={popoverFilterCount === 0}
                >
                  <FilterIcon />
                </Badge>
              </IconButton>
            )}
          </Box>
          <IconButton
            onClick={onExportToCsv}
            sx={{ marginLeft: 'auto', mr: 1, display: 'flex-end' }}
          >
            <SaveAltIcon />
          </IconButton>
          {userManagementEntity !== 'sites' && (
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleAddClick}
              sx={{ minWidth: '150px', maxWidth: '225px', width: '100%' }}
            >
              {`${t(`buttons.addNewEntity`, { entity: entityTitle?.slice(0, -1) })}`}
            </Button>
          )}
        </Box>
      </Box>

      {/* Filter Popover - only rendered if entity has filter config */}
      {filterConfig && (
        <FilterPopover
          anchorEl={filterAnchorEl}
          open={filterPopoverOpen}
          onClose={onFilterClose}
          config={filterConfig}
          dropdownOptions={dropdownOptions}
          onApply={onFilterApply}
          currentFilters={currentFilters}
        />
      )}
    </>
  );
};

export default UserManagementEntityHeader;
